import { gql, useMutation, useQuery } from '@apollo/client';
import { Divider, Stack, Text, useToast } from '@chakra-ui/react';

import { EDigestPreference } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';

import {
  GroupNotifications_UpdateUserGroupLinks,
  GroupNotifications_UpdateUserGroupLinksVariables,
} from './graphql-types/GroupNotifications_UpdateUserGroupLinks';
import { GroupNotifications_UserGroupLinks } from './graphql-types/GroupNotifications_UserGroupLinks';

import { LinkDigestPrefListItem } from '../LinkDigestPrefListItem/LinkDigestPrefListItem';

const USER_GROUP_LINK_QUERY = gql`
  query GroupNotifications_UserGroupLinks {
    currentUser {
      _id
      visibleGroupLinks {
        _id
        digestPreference
        group {
          _id
          name
          isGlobalSingleton
          settings {
            defaultDigestPref
            communityVisibility
          }
        }
      }
    }
  }
`;

const UPDATE_USER_GROUPS_MUTATION = gql`
  mutation GroupNotifications_UpdateUserGroupLinks(
    $input: UpdateUserGroupLinkInput
  ) {
    updateUserGroupLink(input: $input) {
      data {
        _id
      }
    }
  }
`;

export const GroupNotifications: React.FC<{
  isDisabled: boolean;
}> = ({ isDisabled }) => {
  const { data, error, loading } = useQuery<GroupNotifications_UserGroupLinks>(
    USER_GROUP_LINK_QUERY
  );

  const [updateGroupLinkFunc] = useMutation<
    GroupNotifications_UpdateUserGroupLinks,
    GroupNotifications_UpdateUserGroupLinksVariables
  >(UPDATE_USER_GROUPS_MUTATION, {
    refetchQueries: ['GroupNotifications_UserGroupLinks'],
    awaitRefetchQueries: true,
  });

  const toast = useToast();

  const onChange = async (options: {
    userGroupLinkId: string;
    pref: EDigestPreference;
  }): Promise<void> => {
    try {
      await updateGroupLinkFunc({
        variables: {
          input: {
            data: {
              digestPreference: options.pref,
            },
            id: options.userGroupLinkId,
          },
        },
      });

      toast({ status: 'success', description: 'Updated preference' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to update preference.' });
      logError(err);
    }
  };

  if (error) {
    throw error;
  }

  const groupLinks = data?.currentUser?.visibleGroupLinks;

  if (loading || !groupLinks) {
    return null;
  }

  const sorted = groupLinks
    ? groupLinks.slice().sort((a, b) => {
        if (a.group.isGlobalSingleton) {
          return -1;
        }
        if (b.group.isGlobalSingleton) {
          return 1;
        }
        return a.group.name < b.group.name ? -1 : 1;
      })
    : [];

  return (
    <Card overflow="hidden">
      <Stack spacing="0">
        <Text fontSize="sm" p="4" bg="secondary.500" color="textPrimary.500" fontWeight="600">
          Groups
        </Text>
        <Divider />
        <Stack py="4" spacing="2">
          {sorted.map((link, index) => (
            <LinkDigestPrefListItem
              key={link._id}
              link={{
                ...link,
                digestPreference: !!link.digestPreference
                  ? (link.digestPreference as EDigestPreference)
                  : undefined,
                group: {
                  ...link.group,
                  settings: {
                    ...link.group.settings,
                    defaultDigestPref: link.group.settings
                      .defaultDigestPref as EDigestPreference,
                  },
                },
              }}
              onChange={onChange}
              isDisabled={isDisabled}
              isLast={index === sorted.length - 1}
            />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};
